import { useEffect } from 'react'

import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import * as Sentry from '@sentry/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useUserback } from '@userback/react'
import Userfront from '@userfront/core'
import Cookies from 'js-cookie'
import { usePostHog } from 'posthog-js/react'

import { graphql_call } from '@repo/common/queries/schemas/server_user_query'

import { useBeacon } from '../../hooks/useBeacon'
import { useGetHelpscoutSignature } from '../labs/helpscout'

import { mutation_confirm_email, mutation_reset_password } from './auth_graphql'

// eslint-disable-next-line @cspell/spellchecker
void Userfront.init('vbq7rrzb')

export type AuthDataType =
  | {
      email?: string
      uuid?: string
      is_support: false
      is_member: false
      is_email_confirmed: false
      is_authenticated: false
      newsletter_opt_in: false
      terms_of_service_agreed_version: undefined
    }
  | {
      email: string
      uuid: string
      is_support: boolean
      is_member: boolean
      is_email_confirmed: boolean
      is_authenticated: boolean
      newsletter_opt_in: boolean
      terms_of_service_agreed_version: string | undefined
    }

export type UserfrontCustomDataType = {
  newsletter_opt_in?: boolean
  terms_of_service_agreed_version?: string
}

export const keys = createQueryKeyStore({
  user: {
    data: null,
    helpscout_signature: null,
  },
})

export function useRefresh() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      Userfront.tokens.refresh()

      await new Promise((resolve) => setTimeout(resolve, 5000))
      await queryClient.invalidateQueries({ queryKey: keys.user.data.queryKey })
      // eslint-disable-next-line no-console
      console.log('Token Refreshed')
    },
  })
}

// eslint-disable-next-line complexity
export function useIdentify() {
  const posthog = usePostHog()
  const beacon = useBeacon()
  const { identify: userbackIdentify } = useUserback()
  const { data: signature } = useGetHelpscoutSignature()
  const { data: user } = useAuth()
  const replay_url = posthog.get_session_replay_url()

  // for beacon because of the server signature
  useEffect(() => {
    if (
      beacon_has_been_identified == false &&
      user?.is_authenticated &&
      signature
    ) {
      beacon_has_been_identified = true
      beacon('identify', {
        email: user.email,
        user_id: user.uuid,
        signature,
      })
    }
  }, [beacon, signature, user?.email, user?.is_authenticated, user?.uuid])

  // for all others
  useEffect(() => {
    if (user_has_been_identified == false && user?.is_authenticated) {
      user_has_been_identified = true

      posthog.identify(user.uuid, { email: user.email })
      Sentry.setUser({
        id: user.uuid,
        email: user.email,
      })
      userbackIdentify(user.uuid, {
        email: user.email,
        posthog: replay_url,
      })
    }
  }, [
    userbackIdentify,
    posthog,
    replay_url,
    user?.email,
    user?.is_authenticated,
    user?.uuid,
  ])
}

let made_auth_token = false
function useMakeAdminToken() {
  return function make_admin_token_check() {
    if (made_auth_token == false && Userfront.user.hasRole?.('admin')) {
      made_auth_token = true
      // eslint-disable-next-line no-console
      console.log('Making admin token')
      Cookies.set(
        Userfront.tokens.accessTokenName,
        Userfront.tokens.accessToken,
        {
          expires: 7,
          secure: true,
          sameSite: 'Lax',
          domain: '.fission.xyz',
        },
      )
    }
  }
}

let beacon_has_been_identified = false
let user_has_been_identified = false
export function testingRest() {
  user_has_been_identified = false
  beacon_has_been_identified = false
  made_auth_token = false
}

// eslint-disable-next-line complexity
function check_role(role: string, include_admin: boolean = false): boolean {
  return (
    ((Userfront.user.hasRole?.(role) as boolean | undefined) ?? false) ||
    (include_admin &&
      ((Userfront.user.hasRole?.('admin') as boolean | undefined) ?? false))
  )
}

export function useAuth() {
  const make_admin_token_check = useMakeAdminToken()

  const results = useQuery<AuthDataType>({
    queryKey: keys.user.data.queryKey,
    queryFn: () => {
      const custom_data = (Userfront.user.data ?? {}) as UserfrontCustomDataType
      const data: AuthDataType = {
        email: Userfront.user.email ?? '',
        uuid: Userfront.user.userUuid ?? '',
        is_email_confirmed: Boolean(Userfront.user.isEmailConfirmed),
        is_authenticated: Boolean(Userfront.user.userUuid),
        is_support: check_role('support'),
        is_member: check_role('member', true),
        newsletter_opt_in: custom_data.newsletter_opt_in ?? false,
        terms_of_service_agreed_version:
          custom_data.terms_of_service_agreed_version,
      }
      make_admin_token_check()
      return data
    },
  })
  return {
    ...results,
    switch<T>(is_auth: T, not_auth: T) {
      return results.data?.is_authenticated ? is_auth : not_auth
    },
  }
}

export function useIsAuthenticated() {
  const { data } = useAuth()
  return data?.is_authenticated ?? false
}

export function useSignup({
  terms_of_service_version,
}: {
  terms_of_service_version: string
}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      email,
      password,
      newsletter_opt_in,
    }: {
      email: string
      password: string
      newsletter_opt_in: boolean
    }) => {
      await Userfront.signup({
        method: 'password',
        email,
        password,
        redirect: false,
        options: {
          noSignupEmail: true,
        },
        data: {
          newsletter_opt_in,
          terms_of_service_agreed_version: terms_of_service_version,
        } satisfies UserfrontCustomDataType,
      })
      await queryClient.invalidateQueries({ queryKey: keys.user._def })
    },
  })
}

export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string
      password: string
    }) => {
      await Userfront.login({
        method: 'password',
        email,
        password,
        redirect: false,
      })
      await queryClient.invalidateQueries({ queryKey: keys.user._def })
    },
  })
}

export function useConfirmEmail() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ uuid, token }: { uuid: string; token: string }) => {
      await Userfront.login({
        method: 'link',
        uuid,
        token,
        redirect: false,
      })
      await queryClient.invalidateQueries({ queryKey: keys.user._def })
    },
  })
}

export function useResetPassword() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      method: 'jwt' | 'link'
      existingPassword?: string
      password: string
      token?: string
      uuid?: string
    }) => {
      await Userfront.updatePassword({
        ...args,
        redirect: false,
      })
      if (args.method === 'link') {
        await queryClient.invalidateQueries({
          queryKey: keys.user.data.queryKey,
        })
      }
    },
  })
}

export function useRequestResetLink() {
  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      return graphql_call({
        query: mutation_reset_password,
        variables: {
          input: {
            email,
          },
        },
      })
    },
  })
}

export function useRequestConfirmEmail() {
  return useMutation({
    mutationFn: async () => {
      return graphql_call({
        query: mutation_confirm_email,
      })
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  const posthog = usePostHog()

  return useMutation({
    mutationFn: async () => {
      posthog.reset()
      await Userfront.logout({ redirect: '/' })
      await queryClient.invalidateQueries({ queryKey: keys.user._def })
    },
  })
}
